import { useState } from 'react';
import { CheckoutSection } from './CheckoutSection';
import { ECheckFormData } from '../../types';

interface ECheckSectionProps {
  eCheck: ECheckFormData;
  onChange: (data: ECheckFormData) => void;
  totalAmount: number;
}

export function ECheckSection({ eCheck, onChange, totalAmount }: ECheckSectionProps) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const inputClass = (field: string, value: string) => {
    const base = 'w-full rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent';
    if (touched[field] && !value) {
      return `${base} border-2 border-red-500 bg-red-50`;
    }
    return `${base} border border-gray-300`;
  };

  const accountMismatch =
    touched['achAccountConfirm'] &&
    eCheck.achAccountConfirm !== '' &&
    eCheck.achAccountNumber !== eCheck.achAccountConfirm;

  return (
    <CheckoutSection title="eCheck Payment">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="ach_firstname" className="block text-sm font-medium text-gray-700 mb-1">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            id="ach_firstname"
            type="text"
            required
            className={inputClass('achFirstName', eCheck.achFirstName)}
            value={eCheck.achFirstName}
            onChange={(e) => onChange({ ...eCheck, achFirstName: e.target.value })}
            onBlur={() => handleBlur('achFirstName')}
          />
        </div>
        <div>
          <label htmlFor="ach_lastname" className="block text-sm font-medium text-gray-700 mb-1">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            id="ach_lastname"
            type="text"
            required
            className={inputClass('achLastName', eCheck.achLastName)}
            value={eCheck.achLastName}
            onChange={(e) => onChange({ ...eCheck, achLastName: e.target.value })}
            onBlur={() => handleBlur('achLastName')}
          />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="ach_routing" className="block text-sm font-medium text-gray-700 mb-1">
            Routing Number <span className="text-red-500">*</span>
          </label>
          <input
            id="ach_routing"
            type="text"
            inputMode="numeric"
            required
            maxLength={9}
            className={inputClass('achRoutingNumber', eCheck.achRoutingNumber)}
            value={eCheck.achRoutingNumber}
            onChange={(e) => onChange({ ...eCheck, achRoutingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) })}
            onBlur={() => handleBlur('achRoutingNumber')}
            placeholder="9-digit routing number"
          />
          {touched['achRoutingNumber'] && eCheck.achRoutingNumber !== '' && eCheck.achRoutingNumber.length !== 9 && (
            <p className="mt-1 text-sm text-red-600">Routing number must be exactly 9 digits</p>
          )}
        </div>
        <div>
          <label htmlFor="ach_account" className="block text-sm font-medium text-gray-700 mb-1">
            Account Number <span className="text-red-500">*</span>
          </label>
          <input
            id="ach_account"
            type="text"
            inputMode="numeric"
            required
            className={inputClass('achAccountNumber', eCheck.achAccountNumber)}
            value={eCheck.achAccountNumber}
            onChange={(e) => onChange({ ...eCheck, achAccountNumber: e.target.value.replace(/\D/g, '').slice(0, 17) })}
            onBlur={() => handleBlur('achAccountNumber')}
          />
          {touched['achAccountNumber'] && eCheck.achAccountNumber !== '' && eCheck.achAccountNumber.length < 4 && (
            <p className="mt-1 text-sm text-red-600">Account number must be at least 4 digits</p>
          )}
        </div>
        <div>
          <label htmlFor="ach_account_confirm" className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Account Number <span className="text-red-500">*</span>
          </label>
          <input
            id="ach_account_confirm"
            type="text"
            inputMode="numeric"
            required
            className={
              accountMismatch
                ? 'w-full rounded-lg px-4 py-2.5 border-2 border-red-500 bg-red-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                : inputClass('achAccountConfirm', eCheck.achAccountConfirm)
            }
            value={eCheck.achAccountConfirm}
            onChange={(e) => onChange({ ...eCheck, achAccountConfirm: e.target.value.replace(/\D/g, '').slice(0, 17) })}
            onBlur={() => handleBlur('achAccountConfirm')}
          />
          {accountMismatch && (
            <p className="mt-1 text-sm text-red-600">Account numbers do not match</p>
          )}
        </div>
        <div className="md:col-span-2">
          <span className="block text-sm font-medium text-gray-700 mb-2">
            Account Type <span className="text-red-500">*</span>
          </span>
          <div className="flex gap-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="ach_type"
                value="C"
                checked={eCheck.achAccountType === 'C'}
                onChange={() => onChange({ ...eCheck, achAccountType: 'C' })}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Checking</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="ach_type"
                value="S"
                checked={eCheck.achAccountType === 'S'}
                onChange={() => onChange({ ...eCheck, achAccountType: 'S' })}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Savings</span>
            </label>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Total Charges:</span>
          <span className="text-lg font-semibold text-gray-900">${totalAmount.toFixed(2)}</span>
        </div>
      </div>
    </CheckoutSection>
  );
}
